/**
 * SAPPT Design Tokens — Admin Frontend (Indigo brand)
 *
 * 이 파일은 SAPPT 디자인 시스템의 단일 진실 공급원(Single Source of Truth)입니다.
 * 모든 색상/간격/타이포/반경/그림자 값은 여기서 import 해서 사용해야 하며,
 * 컴포넌트 안에 hex 코드를 인라인으로 적지 마세요.
 *
 * 4px 그리드를 따르며 partner-frontend/src/theme/tokens.ts 와 키 구조가 동일합니다.
 * (브랜드 색만 다름)
 */

// ──────────────────────────────────────────────
// Brand (Indigo) — Admin 전용
// ──────────────────────────────────────────────
export const brand = {
  primary: '#4f46e5',
  primaryHover: '#6366f1',
  primaryActive: '#4338ca',
  primarySoft: '#a5b4fc',
  primarySoftBg: '#eef2ff',
  primaryGradient: 'linear-gradient(135deg, #4f46e5, #6366f1)',
  primaryShadow: '0 2px 8px rgba(79,70,229,0.25)',
  primaryRing: 'rgba(79,70,229,0.08)',
} as const;

// ──────────────────────────────────────────────
// Gray Scale (slate 계열)
// ──────────────────────────────────────────────
export const gray = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
} as const;

// ──────────────────────────────────────────────
// State Colors
// ──────────────────────────────────────────────
export const state = {
  success: '#10b981',
  successSoft: '#dcfce7',
  successText: '#15803d',
  warning: '#f59e0b',
  warningSoft: '#fef3c7',
  warningText: '#92400e',
  error: '#ef4444',
  errorSoft: '#fee2e2',
  errorText: '#b91c1c',
  info: '#0ea5e9',
  infoSoft: '#e0f2fe',
  infoText: '#0369a1',
} as const;

// ──────────────────────────────────────────────
// Spacing — 4px Grid
// ──────────────────────────────────────────────
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  base: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64,
} as const;

// ──────────────────────────────────────────────
// Border Radius
// ──────────────────────────────────────────────
export const radius = {
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 20,
  full: 9999,
} as const;

// ──────────────────────────────────────────────
// Shadows
// ──────────────────────────────────────────────
export const shadow = {
  sm: '0 1px 2px rgba(0,0,0,0.04)',
  md: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.03)',
  lg: '0 4px 16px rgba(0,0,0,0.08)',
  xl: '0 8px 28px rgba(0,0,0,0.12)',
  brand: '0 4px 20px rgba(79,70,229,0.25)',
} as const;

// ──────────────────────────────────────────────
// Typography
// ──────────────────────────────────────────────
export const font = {
  /* 한글 폴백(Apple SD Gothic Neo·Malgun Gothic)이 없으면 Pretendard 로드 실패 시
     -apple-system→Segoe UI→Roboto 가 모두 한글이 없어 굴림/Times 로 떨어진다 (2026-08 전수검사) */
  family: "'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, "
        + "'Apple SD Gothic Neo', 'Segoe UI', Roboto, 'Malgun Gothic', sans-serif",
  /* 실제 설치 가능성이 높은 것부터 — D2Coding 은 사내 PC 에만 있어 뒤로 뺀다.
     숫자 자릿수만 맞추면 되는 칸은 모노 대신 font-variant-numeric: tabular-nums 를 쓸 것 */
  mono: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'D2Coding', monospace",
  size: {
    xs: 11,
    sm: 12,
    base: 14,
    md: 15,
    lg: 16,
    xl: 18,
    '2xl': 22,
    '3xl': 28,
  },
  weight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.4,
    base: 1.6,
    relaxed: 1.8,
  },
} as const;

// ──────────────────────────────────────────────
// Semantic aliases (의미 기반 단축 키)
// ──────────────────────────────────────────────
export const color = {
  bg: '#ffffff',
  bgLayout: gray[100],
  bgSubtle: gray[50],
  bgElevated: '#ffffff',
  border: gray[200],
  borderSubtle: gray[100],
  text: gray[800],
  textSecondary: gray[500],
  textTertiary: gray[400],
  textOnPrimary: '#ffffff',
} as const;

// ──────────────────────────────────────────────
// Semantic Colors (의미 기반)
// ──────────────────────────────────────────────
export const semantic = {
  // 차트 팔레트
  chart: ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6'],
  // 아이콘 강조색 (MasterPage 등)
  iconBlue:   '#0ea5e9',
  iconGreen:  '#10b981',
  iconAmber:  '#f59e0b',
  iconRed:    '#ef4444',
  iconPurple: '#8b5cf6',
  iconTeal:   '#14b8a6',
} as const;

// ──────────────────────────────────────────────
// 모달 표준 폭
// ──────────────────────────────────────────────
export const modalWidth = {
  xs:  380,
  sm:  480,
  md:  600,
  lg:  800,
  xl:  1000,
  '2xl': 1200,
} as const;

// ──────────────────────────────────────────────
// 레이아웃 상수
// ──────────────────────────────────────────────
export const layout = {
  headerHeight: 44,
  sidebarWidth: 208,
  sidebarCollapsed: 48,
  /* MainLayout Content 패딩. useViewportFit 이 하단 예약값으로 같은 값을 읽는다 —
     둘이 어긋나면 표 높이 실측이 틀어져 문서 세로스크롤이 생긴다 (2026-09-03 밀도 키트) */
  contentPadding: 8,
} as const;

// ──────────────────────────────────────────────
// 밀도 키트 v3 (2026-09-03) — '장비 콘솔' 밀도
//
// 36화면 전수 실측 근거: 표 화면은 첫 데이터 행까지 평균 358px(1600 기준, 뷰포트 40%)를
// 껍데기로 썼다. 층이 겹칠수록 각 층의 기본값이 그대로 누적되므로
// **페이지를 고치지 않고 층의 두께만 정한다.** 값은 theme/index.ts 가 antd 토큰으로 흘려보내고,
// index.css 에는 토큰으로 표현할 수 없는 것(색·배경·액센트)만 남긴다.
//
// 함정: 표의 가로 패딩은 8을 상한으로 둔다 — 넓히면 열이 좁아져 줄바꿈으로 행이 되레 커진다.
// ──────────────────────────────────────────────
export const density = {
  fontSize: 13,
  fontSizeSmall: 12,
  lineHeight: 1.45,
  controlHeight: 28,
  controlHeightSM: 22,

  menuItemHeight: 30,
  menuItemMarginBlock: 1,
  menuItemMarginInline: 6,
  menuItemPaddingInline: 12,
  menuIconSize: 14,

  cardHeaderHeight: 32,
  cardHeaderPadding: 10,
  cardBodyPadding: 10,

  tableCellPaddingBlock: 4,
  tableCellPaddingInline: 8,   // 상한 — 넘기지 말 것

  formItemMarginBottom: 10,
  formLabelHeight: 20,

  /* 블록(카드) 사이 세로 간격 — 실측에서 0/12/16/24 네 값이 섞여 있었다 */
  blockGap: 8,
} as const;

export const tokens = { brand, gray, state, space, radius, shadow, font, color, semantic, modalWidth, layout, density } as const;
export type Tokens = typeof tokens;
